import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead, buildArticleSchema } from '../components/SEOHead';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Sample post for demo
  const samplePost = {
    title: 'Beneficios de aprender español desde pequeños',
    title_en: 'Benefits of Learning Spanish from a Young Age',
    content: `
      <h2>La importancia del aprendizaje temprano</h2>
      <p>Los estudios demuestran que los niños que comienzan a aprender un segundo idioma antes de los 7 años tienen una mayor facilidad para desarrollar habilidades lingüísticas avanzadas.</p>
      
      <h2>Beneficios cognitivos</h2>
      <p>El bilingüismo no solo mejora las habilidades comunicativas, sino que también potencia otras áreas cognitivas como la memoria, la concentración y la resolución de problemas.</p>
      
      <h2>Nuestro enfoque en Colegio ARCE</h2>
      <p>En Colegio ARCE, utilizamos métodos pedagógicos adaptados a cada edad, combinando el aprendizaje formal con actividades lúdicas que mantienen a los estudiantes motivados y comprometidos.</p>
    `,
    content_en: `
      <h2>The Importance of Early Learning</h2>
      <p>Studies show that children who start learning a second language before age 7 have greater ease in developing advanced linguistic skills.</p>
      
      <h2>Cognitive Benefits</h2>
      <p>Bilingualism not only improves communication skills but also enhances other cognitive areas such as memory, concentration, and problem-solving.</p>
      
      <h2>Our Approach at Colegio ARCE</h2>
      <p>At Colegio ARCE, we use pedagogical methods adapted to each age, combining formal learning with playful activities that keep students motivated and engaged.</p>
    `,
    category: 'Educación',
    created_at: new Date().toISOString(),
    featured_image: 'https://images.pexels.com/photos/5905955/pexels-photo-5905955.jpeg?auto=compress&cs=tinysrgb&w=1200'
  };

  const displayPost = post || samplePost;

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex justify-center items-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <article className="pt-40 pb-20 px-6">
        {displayPost && (
          <SEOHead
            titleES={`${displayPost.title} — Blog Colegio ARCE`}
            titleEN={`${displayPost.title_en || displayPost.title} — Colegio ARCE Blog`}
            descES={displayPost.excerpt || displayPost.title}
            descEN={displayPost.excerpt_en || displayPost.excerpt || displayPost.title}
            path={`/blog/${slug}`}
            schema={buildArticleSchema(displayPost)}
            breadcrumbs={[
              { nameES: 'Inicio', nameEN: 'Home', path: '/' },
              { nameES: 'Blog', nameEN: 'Blog', path: '/blog' },
              { nameES: displayPost.title, nameEN: displayPost.title_en || displayPost.title, path: `/blog/${slug}` }
            ]}
          />
        )}
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] mb-8 transition-colors"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'es' ? 'Volver al blog' : 'Back to blog'}
          </Link>

          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-[#E63946]/10 text-[#E63946] text-sm font-medium rounded-full">
                {displayPost.category}
              </span>
              <span className="flex items-center gap-2 text-sm text-[#86868B]">
                <Calendar className="h-4 w-4" />
                {formatDate(displayPost.created_at)}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#1D1D1F] mb-6" data-testid="blog-post-title">
              {language === 'es' ? displayPost.title : (displayPost.title_en || displayPost.title)}
            </h1>
          </header>

          {/* Featured Image */}
          {displayPost.featured_image && (
            <div className="aspect-video rounded-2xl overflow-hidden mb-12">
              <img 
                src={displayPost.featured_image} 
                alt={language === 'es' ? displayPost.title : (displayPost.title_en || displayPost.title)}
                loading="lazy"
                width="1200"
                height="675"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none prose-headings:font-semibold prose-headings:text-[#1D1D1F] prose-p:text-[#86868B] prose-a:text-[#E63946]"
            dangerouslySetInnerHTML={{ 
              __html: DOMPurify.sanitize(
                (language === 'es' 
                  ? displayPost.content 
                  : (displayPost.content_en || displayPost.content)
                )?.replace(/&nbsp;/g, ' ') || ''
              )
            }}
            data-testid="blog-post-content"
          />

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-black/5">
            <button className="flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] transition-colors">
              <Share2 className="h-5 w-5" />
              {language === 'es' ? 'Compartir artículo' : 'Share article'}
            </button>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
